import type {AsignacionDeExportacion, ContenedorAsignado} from '@cnc/contracts';
import {useState} from 'react';
import {Link} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {useAjustarAsignacion, useAsignacion} from '../api/reservas';
import {tr} from '../i18n/idioma';
import {fecha, numero, textoOGuion} from '../utilidades/formato';
import {ElegirMinas} from './elegir-minas';
import {AYUDA_ASIGNACION as AS} from './ayuda-exportacion';
import {NotaDeAyuda} from './nota-de-ayuda';
import {usePermisos} from './sesion-contexto';

const boton =
  'rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50';
const botonSuave = 'rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel disabled:opacity-50';
const campo = 'rounded-md border border-linea px-2 py-1 text-sm text-tinta';

// Contenedores de la exportacion: la app los arma de a 28 Big Bags del mismo
// exportador, sin pasar de 28 toneladas humedas ni de los contenedores de la
// reserva. Un editor puede revisar y ajustar cada contenedor.
export function AsignacionDeContenedores({codigo}: {codigo: string}) {
  const permisos = usePermisos();
  const asignacion = useAsignacion(codigo);
  const ajustar = useAjustarAsignacion(codigo);
  const [mensaje, setMensaje] = useState<{texto: string; error: boolean} | null>(null);

  const ejecutar = async (ajuste: Parameters<typeof ajustar.mutateAsync>[0]) => {
    setMensaje(null);
    try {
      const texto = await ajustar.mutateAsync(ajuste);
      if (texto) setMensaje({texto, error: false});
      return true;
    } catch (error) {
      setMensaje({
        texto:
          error instanceof ErrorDeApi
            ? [error.message, ...(error.detalles ?? [])].join(' ')
            : tr('No se pudo hacer el cambio.', 'Could not make the change.'),
        error: true,
      });
      return false;
    }
  };

  if (asignacion.isError)
    return (
      <div className="rounded-lg border border-linea bg-white p-5 text-sm text-alerta">{asignacion.error.message}</div>
    );
  if (!asignacion.data) return null;
  const a = asignacion.data;

  return (
    <div className="rounded-lg border border-linea bg-white p-5" aria-label={tr('Contenedores', 'Containers')}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="font-medium">{tr('Contenedores y reserva', 'Containers and booking')}</h2>
        <span className="text-xs text-apagado">
          {a.limites.mineros
            ? tr(
                `Por contenedor: hasta ${numero(a.limites.tonHumedasPorContenedor)} t húmedas y ${numero(a.limites.bolsasPorContenedor)} Big Bags, mínimo ${a.limites.mineros.altoTenorMinimo} de alto tenor y tenor promedio de ${numero(a.limites.mineros.tenorMinimo)} g/t (hasta ${numero(a.limites.mineros.tenorMaximo)} en casos puntuales)`,
                `Per container: up to ${numero(a.limites.tonHumedasPorContenedor)} wet t and ${numero(a.limites.bolsasPorContenedor)} Big Bags, at least ${a.limites.mineros.altoTenorMinimo} high grade and an average grade of ${numero(a.limites.mineros.tenorMinimo)} g/t (up to ${numero(a.limites.mineros.tenorMaximo)} in specific cases)`,
              )
            : tr(
                `${numero(a.limites.bolsasPorContenedor)} Big Bags y hasta ${numero(a.limites.tonHumedasPorContenedor)} t húmedas por contenedor`,
                `${numero(a.limites.bolsasPorContenedor)} Big Bags and up to ${numero(a.limites.tonHumedasPorContenedor)} wet t per container`,
              )}
          {a.limites.mineros && <NotaDeAyuda titulo={tr('Mineros', 'Mineros')} texto={AS.reglaMineros} />}
        </span>
      </div>

      <ResumenDeReserva asignacion={a} />

      {a.motivo && <p className="mt-3 rounded-md bg-papel px-3 py-2 text-sm text-apagado">{a.motivo}</p>}

      {/* Alertas de Mineros (Daniel, 23/10/2026): por qué no alcanza y qué pedirle a la planta. */}
      {a.alertas.length > 0 && (
        <div
          className="mt-3 rounded-md border border-dorado/60 bg-dorado-claro px-3 py-2 text-sm"
          role="alert"
          data-prueba="alertas-de-mineros"
        >
          {a.alertas.map(texto => (
            <p key={texto}>⚠ {texto}</p>
          ))}
          {a.sinResultados.length > 0 && (
            <p className="mt-1 text-xs text-apagado">
              {tr('Sin resultados completos:', 'Without complete results:')} {a.sinResultados.slice(0, 40).join(', ')}
              {a.sinResultados.length > 40 &&
                tr(` y ${a.sinResultados.length - 40} más`, ` and ${a.sinResultados.length - 40} more`)}
            </p>
          )}
        </div>
      )}

      {a.reserva &&
        a.minas.length > 0 &&
        (permisos.editar ? (
          <div className="mt-3 rounded-md bg-papel px-3 py-2" role="region" aria-label={tr('Minas', 'Mines')}>
            <ElegirMinas
              minas={a.minas}
              elegidas={a.minasElegidas}
              deshabilitado={ajustar.isPending}
              onCambiar={minas => void ejecutar({tipo: 'minas', minas})}
            />
            <p className="mt-1 text-xs text-apagado">
              {tr(
                `Solo salen las minas de ${a.exportador ?? 'este exportador'}. La elección se guarda en la reserva y también la usa la asignación automática.`,
                `Only ${a.exportador ?? 'this exporter'}'s mines are listed. The choice is saved on the booking and the automatic assignment uses it too.`,
              )}
            </p>
          </div>
        ) : (
          a.minasElegidas.length > 0 && (
            <p className="mt-3 text-sm text-apagado">
              {tr('Minas para asignar:', 'Mines to assign:')}{' '}
              {a.minas
                .filter(m => a.minasElegidas.includes(m.id))
                .map(m => m.nombre)
                .join(', ')}
            </p>
          )
        ))}

      {permisos.editar && a.reserva && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            className={boton}
            disabled={ajustar.isPending}
            onClick={() => void ejecutar({tipo: 'asignar'})}
          >
            {tr('Asignar ahora', 'Assign now')}
          </button>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={a.reserva.asignacionAutomatica}
              disabled={ajustar.isPending}
              onChange={e => void ejecutar({tipo: 'automatica', activa: e.target.checked})}
            />
            {a.limites.mineros
              ? tr(
                  'Asignar automáticamente cuando haya Big Bags listos para un contenedor',
                  'Assign automatically when there are ready Big Bags for a container',
                )
              : tr(
                  'Asignar automáticamente cuando haya 28 Big Bags disponibles',
                  'Assign automatically when 28 Big Bags are available',
                )}
            <NotaDeAyuda texto={AS.automatica} dentroDeEtiqueta />
          </label>
        </div>
      )}
      {mensaje && (
        <p className={`mt-3 text-sm ${mensaje.error ? 'text-alerta' : 'text-verde-oscuro'}`} role="status">
          {mensaje.texto}
        </p>
      )}

      {a.contenedores.length === 0 ? (
        <p className="mt-4 text-sm text-apagado">
          {tr('Todavía no hay contenedores asignados.', 'No containers assigned yet.')}
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          {a.contenedores.map(c => (
            <Contenedor
              key={c.id}
              contenedor={c}
              editable={permisos.editar}
              pendiente={ajustar.isPending}
              onAjustar={ejecutar}
            />
          ))}
        </div>
      )}
      {a.sinContenedor > 0 && (
        <p className="mt-3 text-xs text-apagado">
          {tr(
            `${numero(a.sinContenedor)} Big Bags de la exportación no están en ningún contenedor.`,
            `${numero(a.sinContenedor)} Big Bags of this export are not in any container.`,
          )}
        </p>
      )}
    </div>
  );
}

function ResumenDeReserva({asignacion: a}: {asignacion: AsignacionDeExportacion}) {
  const r = a.reserva;
  if (!r)
    return (
      <p className="mt-2 text-sm text-apagado">
        {tr('Esta exportación no está vinculada a una reserva. ', 'This export is not linked to a booking. ')}
        <Link to="/seguimiento/reservas" className="text-verde hover:underline">
          {tr('Ir a reservas', 'Go to bookings')}
        </Link>
      </p>
    );
  const completos = a.contenedores.filter(c => c.completo).length;
  const faltan = Math.max(0, r.contenedores - completos);
  return (
    <div className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-4">
      <div>
        <span className="text-apagado">
          {tr('Reserva:', 'Booking:')}
          <NotaDeAyuda titulo={tr('Reserva', 'Booking')} texto={AS.reserva} />
        </span>{' '}
        <Link to={`/seguimiento/reservas?id=${r.id}`} className="font-medium text-verde hover:underline">
          {r.navieraEtiqueta} {r.numero}
        </Link>
      </div>
      <div>
        <span className="text-apagado">
          {tr('Salida:', 'Departure:')}
          <NotaDeAyuda titulo={tr('Salida', 'Departure')} texto={AS.salida} />
        </span>{' '}
        {fecha(r.etd)}{' '}
        <span className="text-xs text-apagado">
          (
          {r.diasParaSalida >= 0
            ? tr(`en ${r.diasParaSalida} días`, `in ${r.diasParaSalida} days`)
            : tr('ya salió', 'departed')}
          )
        </span>
      </div>
      <div>
        <span className="text-apagado">
          {tr('Contenedores completos:', 'Full containers:')}
          <NotaDeAyuda titulo={tr('Contenedores completos', 'Full containers')} texto={AS.completos} />
        </span>{' '}
        <span className="font-medium">
          {numero(completos)} / {numero(r.contenedores)}
        </span>
        {faltan > 0 && <span className="text-xs text-alerta"> · {tr(`faltan ${faltan}`, `${faltan} missing`)}</span>}
      </div>
      <div>
        <span className="text-apagado">
          {tr(
            `Disponibles de ${a.exportador ?? 'este exportador'}:`,
            `Available from ${a.exportador ?? 'this exporter'}:`,
          )}
          <NotaDeAyuda titulo={tr('Disponibles', 'Available')} texto={AS.disponibles} />
        </span>{' '}
        <span className="font-medium">{numero(a.disponiblesDelExportador)}</span>
      </div>
    </div>
  );
}

function Contenedor({
  contenedor: c,
  editable,
  pendiente,
  onAjustar,
}: {
  contenedor: ContenedorAsignado;
  editable: boolean;
  pendiente: boolean;
  onAjustar: (
    ajuste:
      | {tipo: 'agregar'; contenedorId: number; bbNo: string}
      | {tipo: 'quitar'; bbNo: string}
      | {tipo: 'liberar'; contenedorId: number}
      | {tipo: 'numero'; contenedorId: number; numero: string},
  ) => Promise<boolean>;
}) {
  const [abierto, setAbierto] = useState(false);
  const [bbNo, setBbNo] = useState('');
  const [numeroNuevo, setNumeroNuevo] = useState<string | null>(null);
  const pasado = c.tonHumedas > 28 + 1e-9;

  return (
    <div className={`rounded-md border px-3 py-2 ${c.completo ? 'border-linea' : 'border-dorado/60'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setAbierto(v => !v)}
            className="font-medium text-verde hover:underline"
            aria-expanded={abierto}
          >
            {abierto ? '▾' : '▸'} {c.numero}
          </button>
          {c.numeroProvisional && (
            <span className="rounded-full bg-papel px-2 py-0.5 text-xs text-apagado">
              {tr('número provisional', 'provisional number')}
            </span>
          )}
          <span className="text-xs text-apagado">
            {c.origen === 'auto' ? tr('armado por la app', 'built by the app') : tr('armado a mano', 'built manually')}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          {c.tenor !== null ? (
            <>
              <span className="text-apagado">{numero(c.bigBags)} BB</span>
              <span className={(c.altoTenor ?? 0) >= 4 ? 'text-verde-oscuro' : 'text-alerta'}>
                {tr(`${numero(c.altoTenor)} de alto tenor`, `${numero(c.altoTenor)} high grade`)}
              </span>
              <span className={c.tenor >= 800 && c.tenor <= 1000 ? 'text-verde-oscuro' : 'text-alerta'}>
                {numero(c.tenor, 1)} g/t
              </span>
            </>
          ) : (
            <span className={c.completo ? 'text-verde-oscuro' : 'text-alerta'}>{numero(c.bigBags)} / 28 BB</span>
          )}
          <span className={pasado ? 'text-alerta' : 'text-apagado'}>
            {numero(c.tonHumedas, 3)} {tr('t húmedas', 'wet t')}
          </span>
          <span className="text-apagado">
            {numero(c.tonSecas, 3)} {tr('t secas', 'dry t')}
          </span>
        </div>
      </div>
      {/* Mineros (Daniel, 23/10/2026): pesos, humedad, tenores por grupo y oro. */}
      {c.tenor !== null && (
        <dl
          className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-apagado"
          aria-label={tr(`Resumen de ${c.numero}`, `Summary of ${c.numero}`)}
          data-prueba="resumen-mineros"
        >
          <Dato
            titulo={tr('Humedad', 'Moisture')}
            valor={c.tonHumedas > 0 ? `${numero(((c.tonHumedas - c.tonSecas) / c.tonHumedas) * 100, 2)} %` : '—'}
          />
          <Dato
            titulo={tr('Tenor alto', 'High grade')}
            valor={c.tenorAlto === null ? '—' : `${numero(c.tenorAlto, 1)} g/t`}
          />
          <Dato
            titulo={tr('Tenor bajo', 'Low grade')}
            valor={c.tenorBajo === null ? '—' : `${numero(c.tenorBajo, 1)} g/t`}
          />
          <Dato titulo={tr('Tenor del contenedor', 'Container grade')} valor={`${numero(c.tenor, 1)} g/t`} />
          <Dato titulo={tr('Oro', 'Gold')} valor={c.oroG === null ? '—' : `${numero(c.oroG, 2)} g`} />
          <Dato titulo={tr('Onzas', 'Ounces')} valor={c.oroOz === null ? '—' : `${numero(c.oroOz, 3)} oz`} />
        </dl>
      )}

      {abierto && (
        <div className="mt-2">
          {editable && (
            <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
              {numeroNuevo === null ? (
                <button
                  type="button"
                  className={botonSuave}
                  onClick={() => setNumeroNuevo(c.numeroProvisional ? '' : c.numero)}
                >
                  {tr('Poner número de contenedor', 'Set container number')}
                </button>
              ) : (
                <form
                  className="flex items-center gap-2"
                  onSubmit={e => {
                    e.preventDefault();
                    void onAjustar({tipo: 'numero', contenedorId: c.id, numero: numeroNuevo}).then(ok => {
                      if (ok) setNumeroNuevo(null);
                    });
                  }}
                >
                  <input
                    value={numeroNuevo}
                    onChange={e => setNumeroNuevo(e.target.value)}
                    placeholder="ABCU1234567"
                    aria-label={tr('Número de contenedor', 'Container number')}
                    className={campo}
                    required
                  />
                  <button type="submit" className={boton} disabled={pendiente}>
                    {tr('Guardar', 'Save')}
                  </button>
                  <button type="button" className={botonSuave} onClick={() => setNumeroNuevo(null)}>
                    {tr('Cancelar', 'Cancel')}
                  </button>
                </form>
              )}
              {(c.bigBags < 28 || c.tenor !== null) && (
                <form
                  className="flex items-center gap-2"
                  onSubmit={e => {
                    e.preventDefault();
                    void onAjustar({tipo: 'agregar', contenedorId: c.id, bbNo}).then(ok => {
                      if (ok) setBbNo('');
                    });
                  }}
                >
                  <input
                    value={bbNo}
                    onChange={e => setBbNo(e.target.value)}
                    placeholder={tr('BB # disponible', 'Available BB #')}
                    aria-label={tr('Big Bag para agregar', 'Big Bag to add')}
                    className={campo}
                    required
                  />
                  <button type="submit" className={botonSuave} disabled={pendiente}>
                    {tr('+ Agregar', '+ Add')}
                  </button>
                </form>
              )}
              <button
                type="button"
                className={`${botonSuave} text-alerta`}
                disabled={pendiente}
                onClick={() => {
                  if (
                    window.confirm(
                      tr(
                        `¿Liberar el contenedor ${c.numero}? Sus ${c.bigBags} Big Bags vuelven a bodega y la asignación automática de esta exportación se apaga.`,
                        `Release container ${c.numero}? Its ${c.bigBags} Big Bags go back to the warehouse and automatic assignment for this export is turned off.`,
                      ),
                    )
                  )
                    void onAjustar({tipo: 'liberar', contenedorId: c.id});
                }}
              >
                {tr('Liberar contenedor', 'Release container')}
              </button>
            </div>
          )}
          <table className="w-full text-xs">
            <thead className="text-left text-apagado">
              <tr>
                <th className="py-1 font-normal">BB #</th>
                <th className="py-1 font-normal">
                  {tr('Recolección', 'Pickup')}
                  <NotaDeAyuda titulo={tr('Recolección', 'Pickup')} texto={AS.recoleccion} />
                </th>
                {c.tenor !== null ? (
                  <th className="py-1 font-normal">{tr('Producción', 'Production')}</th>
                ) : (
                  <th className="py-1 font-normal">
                    {tr('Entrada a bodega', 'Warehouse entry')}
                    <NotaDeAyuda titulo={tr('Entrada a bodega', 'Warehouse entry')} texto={AS.entradaBodega} />
                  </th>
                )}
                <th className="py-1 text-right font-normal">
                  {tr('T húmedas', 'Wet t')}
                  <NotaDeAyuda titulo={tr('T húmedas', 'Wet t')} texto={AS.tonHumedas} />
                </th>
                <th className="py-1 text-right font-normal">
                  {tr('T secas', 'Dry t')}
                  <NotaDeAyuda titulo={tr('T secas', 'Dry t')} texto={AS.tonSecas} />
                </th>
                {c.tenor !== null && (
                  <>
                    <th className="py-1 text-right font-normal">{tr('Humedad', 'Moisture')}</th>
                    <th className="py-1 text-right font-normal">
                      {tr('Tenor (g/t)', 'Grade (g/t)')}
                      <NotaDeAyuda titulo={tr('Tenor', 'Grade')} texto={AS.tenorMineros} />
                    </th>
                    <th className="py-1 text-right font-normal">{tr('Oro (g)', 'Gold (g)')}</th>
                  </>
                )}
                {editable && <th />}
              </tr>
            </thead>
            <tbody>
              {c.bolsas.map(b => (
                <tr key={b.bbNo} className="border-t border-linea/70">
                  <td className="py-1">
                    <Link to={`/inventario/${encodeURIComponent(b.bbNo)}`} className="text-verde hover:underline">
                      {b.bbNo}
                    </Link>
                  </td>
                  <td className="py-1">{textoOGuion(b.recoleccion)}</td>
                  <td className="py-1">
                    {c.tenor !== null
                      ? b.produccion
                        ? fecha(b.produccion)
                        : '—'
                      : b.entradaBodega
                        ? fecha(b.entradaBodega)
                        : '—'}
                  </td>
                  <td className="py-1 text-right tabular-nums">{numero(b.tonHumedas, 3)}</td>
                  <td className="py-1 text-right tabular-nums">{numero(b.tonSecas, 3)}</td>
                  {c.tenor !== null && (
                    <>
                      <td className="py-1 text-right tabular-nums">
                        {b.tonHumedas > 0 ? `${numero(((b.tonHumedas - b.tonSecas) / b.tonHumedas) * 100, 2)} %` : '—'}
                      </td>
                      <td className="py-1 text-right tabular-nums">
                        {numero(b.tenor, 1)}
                        {b.altoTenor && <span className="ml-1 text-dorado">{tr('alto', 'high')}</span>}
                      </td>
                      <td className="py-1 text-right tabular-nums">{b.oroG === null ? '—' : numero(b.oroG, 2)}</td>
                    </>
                  )}
                  {editable && (
                    <td className="py-1 text-right">
                      <button
                        type="button"
                        className="text-apagado hover:text-alerta"
                        disabled={pendiente}
                        onClick={() => void onAjustar({tipo: 'quitar', bbNo: b.bbNo})}
                        aria-label={tr(`Quitar ${b.bbNo}`, `Remove ${b.bbNo}`)}
                      >
                        ✕
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Un dato del resumen de un contenedor de Mineros.
function Dato({titulo, valor}: {titulo: string; valor: string}) {
  return (
    <div className="flex gap-1">
      <dt>{titulo}:</dt>
      <dd className="tabular-nums text-tinta">{valor}</dd>
    </div>
  );
}
